/**
 * API para Cierre de Ciclo Escolar
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Obtiene la lista de programas académicos con filtros y paginación.
 * @param {Object} params - { id_campus, id_seccion, id_ciclo_escolar, page }
 */
export async function getProgramasCierreCiclo(params = {}) {
  const qs = new URLSearchParams();
  if (params.id_campus) qs.append('id_campus', params.id_campus);
  if (params.id_seccion) qs.append('id_seccion', params.id_seccion);
  if (params.id_ciclo_escolar) qs.append('id_ciclo_escolar', params.id_ciclo_escolar);
  if (params.page) qs.append('page', params.page);

  const res = await fetch(`${API_BASE}/escolar/cierre-ciclo/programas/?${qs.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Error al obtener programas: ${res.status}`);
  return res.json();
}

/**
 * Valida si un programa+ciclo tiene alumnos sin calificación FINAL.
 * @param {number} id_programa
 * @param {number} id_ciclo_escolar
 */
export async function validarCierreCiclo(id_programa, id_ciclo_escolar) {
  const qs = new URLSearchParams({ id_programa, id_ciclo_escolar });
  const res = await fetch(`${API_BASE}/escolar/cierre-ciclo/validar/?${qs.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Error al validar cierre: ${res.status}`);
  return res.json();
}

/**
 * Ejecuta el cierre de ciclo: graba en esc_cardex.
 * @param {number} id_programa
 * @param {number} id_ciclo_escolar
 */
export async function ejecutarCierreCiclo(id_programa, id_ciclo_escolar) {
  const res = await fetch(`${API_BASE}/escolar/cierre-ciclo/ejecutar/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ id_programa, id_ciclo_escolar }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Error al ejecutar cierre: ${res.status}`);
  }
  return res.json();
}

/**
 * Obtiene los ciclos escolares disponibles.
 */
export async function getCiclosEscolares() {
  const res = await fetch(`${API_BASE}/catalogos/ciclos-escolares/`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const data = await res.json();
  const items = Array.isArray(data) ? data : (data.results || []);
  // El serializer expone `id` (PK) y `nombre` (nombre_ciclo). Se conservan los
  // alias por compatibilidad si el backend agrega campos directos en el futuro.
  return items.map((c) => ({
    id: c.id ?? c.id_ciclo_escolar,
    nombre: c.nombre ?? c.nombre_ciclo,
    estado: c.estado_ciclo,
    fecha_inicio: c.fecha_inicio,
    fecha_fin: c.fecha_fin,
    anio: c.anio,
    numero_periodo: c.numero_periodo,
  }));
}

/**
 * Obtiene el ciclo escolar actual detectado por fecha del servidor.
 * Reutiliza el endpoint del módulo financiero ya usado en Reinscripción.
 */
export async function getCicloActual() {
  const res = await fetch(`${API_BASE}/financiero/ciclo-actual/`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return null;
  return res.json();
}

/**
 * Obtiene los ciclos escolares con HISTORIAL de inscripciones.
 * Solo devuelve ciclos donde existe al menos una EscInscripcion(INSCRITO),
 * por lo que son los únicos cerrables.
 * @param {Object} params - { id_campus, id_programa } opcionales
 */
export async function getCiclosDisponiblesCierre(params = {}) {
  const qs = new URLSearchParams();
  if (params.id_campus) qs.append('id_campus', params.id_campus);
  if (params.id_programa) qs.append('id_programa', params.id_programa);
  const url = `${API_BASE}/escolar/cierre-ciclo/ciclos-disponibles/${qs.toString() ? '?' + qs.toString() : ''}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) return [];
  return res.json();
}

/**
 * Obtiene las secciones académicas disponibles.
 */
export async function getSeccionesAcademicas() {
  const res = await fetch(`${API_BASE}/catalogos/secciones-academicas/`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const data = await res.json();
  const items = Array.isArray(data) ? data : (data.results || []);
  return items.map((s) => ({
    id: s.id_seccion,
    nombre: s.nombre_seccion,
  }));
}
